import { useMemo, useState } from 'react'
import Breadcrumbs from '@/components/Breadcrumbs'
import DisclaimerNote from '@/components/DisclaimerNote'
import FaqAccordion from '@/components/FaqAccordion'
import NumberInput from '@/components/ui/NumberInput'
import Slider from '@/components/ui/Slider'
import SectionHeading from '@/components/ui/SectionHeading'
import StatCard from '@/components/ui/StatCard'
import Seo from '@/components/Seo'
import { formatCurrency } from '@/lib/format'
import { breadcrumbSchema, faqPageSchema, softwareApplicationSchema } from '@/lib/schema'

function simulate(principal: number, monthlyRate: number, basePayment: number, extra: number) {
  let balance = principal
  let months = 0
  let totalInterest = 0
  while (balance > 0.01 && months < 600) {
    const interest = balance * monthlyRate
    let principalPaid = basePayment - interest + extra
    if (principalPaid > balance) principalPaid = balance
    balance -= principalPaid
    totalInterest += interest
    months += 1
  }
  return { months, totalInterest }
}

const faqItems = [
  { question: 'How much can I save by paying extra on my mortgage?', answer: 'Even small extra principal payments can save tens of thousands in interest and shave years off a 30-year mortgage, because mortgage interest compounds against your remaining balance.' },
  { question: 'Should I pay off my mortgage early or invest instead?', answer: 'It depends on your mortgage rate versus expected investment returns. If your mortgage rate is low, investing may yield better long-term results, but paying it off guarantees a return equal to your interest rate.' },
  { question: 'Are there penalties for paying off a mortgage early?', answer: 'Some loans include prepayment penalties, though these are less common today. Always check your loan terms before making large extra payments.' },
]

function MortgagePayoff() {
  const [principal, setPrincipal] = useState(320000)
  const [apr, setApr] = useState(6.75)
  const [termYears, setTermYears] = useState(30)
  const [extraPayment, setExtraPayment] = useState(200)

  const results = useMemo(() => {
    const monthlyRate = apr / 100 / 12
    const totalMonths = termYears * 12
    const basePayment = monthlyRate === 0
      ? principal / totalMonths
      : (principal * monthlyRate * Math.pow(1 + monthlyRate, totalMonths)) / (Math.pow(1 + monthlyRate, totalMonths) - 1)

    const base = simulate(principal, monthlyRate, basePayment, 0)
    const withExtra = simulate(principal, monthlyRate, basePayment, extraPayment)

    return {
      basePayment,
      baseMonths: base.months,
      baseInterest: base.totalInterest,
      extraMonths: withExtra.months,
      extraInterest: withExtra.totalInterest,
      monthsSaved: base.months - withExtra.months,
      interestSaved: base.totalInterest - withExtra.totalInterest,
    }
  }, [principal, apr, termYears, extraPayment])

  return (
    <>
      <Seo
        title="Mortgage Payoff Calculator - Extra Payment Savings"
        description="Free mortgage payoff calculator. See how extra monthly principal payments reduce your mortgage term and total interest paid over the life of the loan."
        path="/calculators/mortgage-payoff"
        schema={[
          softwareApplicationSchema('Mortgage Payoff Calculator', 'Calculate mortgage payoff time and interest savings from extra payments.', 'https://www.mintcalculator.com/calculators/mortgage-payoff'),
          faqPageSchema(faqItems),
          breadcrumbSchema([{ name: 'Home', path: '/' }, { name: 'Mortgage Payoff Calculator', path: '/calculators/mortgage-payoff' }]),
        ]}
      />
      <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
        <Breadcrumbs items={[{ name: 'Home', path: '/' }, { name: 'Mortgage Payoff Calculator', path: '/calculators/mortgage-payoff' }]} />
        <SectionHeading
          eyebrow="Loans, Credit & Debt Payoff"
          title="Mortgage Payoff Calculator"
          description="See exactly how much time and interest an extra monthly principal payment saves on your mortgage."
        />

        <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-5">
          <div className="space-y-5 rounded-xl border border-slate-800 bg-slate-900/50 p-5 lg:col-span-2">
            <NumberInput label="Mortgage principal" value={principal} onChange={setPrincipal} prefix="$" step={5000} />
            <Slider label="Interest rate (APR)" value={apr} onChange={setApr} min={2} max={10} step={0.05} formatValue={v => `${v.toFixed(2)}%`} accent="cyan" />
            <Slider label="Loan term (years)" value={termYears} onChange={setTermYears} min={10} max={30} step={1} formatValue={v => `${v} yr`} accent="teal" />
            <NumberInput label="Extra monthly payment" value={extraPayment} onChange={setExtraPayment} prefix="$" step={25} accent="amber" />
          </div>

          <div className="grid grid-cols-2 gap-3 lg:col-span-3">
            <StatCard label="Monthly Payment" value={formatCurrency(results.basePayment)} accent="emerald" size="lg" />
            <StatCard label="New Payoff Time" value={`${(results.extraMonths / 12).toFixed(1)} yrs`} accent="teal" size="lg" />
            <StatCard label="Time Saved" value={`${results.monthsSaved} mo`} accent="cyan" />
            <StatCard label="Interest Saved" value={formatCurrency(results.interestSaved)} accent="amber" />
            <StatCard label="Original Total Interest" value={formatCurrency(results.baseInterest)} accent="slate" />
            <StatCard label="New Total Interest" value={formatCurrency(results.extraInterest)} accent="slate" />
          </div>
        </div>

        <div className="mt-6">
          <DisclaimerNote />
        </div>

        <div className="mt-16 max-w-3xl">
          <SectionHeading title="Frequently Asked Questions" />
          <div className="mt-6">
            <FaqAccordion items={faqItems} />
          </div>
        </div>
      </div>
    </>
  )
}

export default MortgagePayoff
